import { Box, Button, InputAdornment, Modal, TextField } from '@mui/material';
import SearchLoupe from 'assets/icons/search-loupe.svg';
import XMarkIcon from 'assets/icons/xmark.svg';

import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { PrimaryIndustrySearch } from 'components/lib/PrimaryIndustrySearch';
import { castArray } from 'lodash-es';
import { type FC, useEffect, useRef, useState } from 'react';
import {
  PrimaryIndustrySearchMobileAutoCompletePopperStyle,
  PrimaryIndustrySearchMobileAutoCompleteStyle,
  PrimaryIndustrySearchMobileCloseModalButtonStyle,
  PrimaryIndustrySearchMobileModalStyle,
  PrimaryIndustrySearchMobileModalWrapperStyle,
} from './PrimaryIndustrySearchMobile.styles';
import type { PrimaryIndustrySearchMobileProps } from './PrimaryIndustrySearchMobile.types';

const PrimaryIndustrySearchMobile: FC<PrimaryIndustrySearchMobileProps> = ({ ...rest }) => {
  const { formik, fieldName, label, placeholder, state, renderStartAdornment = true } = rest;
  const modalTargetInputRef = useRef<HTMLInputElement>(null);
  const [isMobileAutoCompleteOpen, setIsMobileAutoCompleteOpen] = useState(false);

  const handleOpenModal = () => {
    setIsMobileAutoCompleteOpen(true);
  };

  const handleCloseModal = () => {
    setIsMobileAutoCompleteOpen(false);
  };

  useEffect(() => {
    // Blur the target input, when modal visibility changes.
    modalTargetInputRef.current?.blur();
  }, [isMobileAutoCompleteOpen]);

  // Close the modal when industry selected.
  useEffect(() => {
    handleCloseModal();
  }, [state?.pol_tax_keyword]);

  return (
    <>
      <TextField
        inputRef={modalTargetInputRef}
        fullWidth
        name={fieldName}
        label={label}
        placeholder={placeholder}
        value={state?.pol_tax_keyword || ''}
        onClick={handleOpenModal}
        error={formik.touched?.[fieldName] && Boolean(formik.errors?.[fieldName])}
        helperText={formik.touched?.[fieldName] && formik.errors?.[fieldName]}
        sx={[
          (theme) => theme.mixins.placeholderColor(formik, fieldName),
          (theme) => theme.mixins.errorIconStyle,
        ]}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: renderStartAdornment ? (
              <InputAdornment position="start">
                <SvgIconWrapper
                  src={SearchLoupe}
                  className={
                    formik.touched?.[fieldName] && Boolean(formik.errors?.[fieldName])
                      ? 'error-icon-style'
                      : ''
                  }
                />
              </InputAdornment>
            ) : null,
            endAdornment: null,
            autoComplete: 'off',
          },

          inputLabel: { shrink: true },
        }}
      />
      <Modal
        open={isMobileAutoCompleteOpen}
        sx={PrimaryIndustrySearchMobileModalStyle}
        onClose={handleCloseModal}
      >
        <Box sx={PrimaryIndustrySearchMobileModalWrapperStyle}>
          <Button sx={PrimaryIndustrySearchMobileCloseModalButtonStyle} onClick={handleCloseModal}>
            <SvgIconWrapper src={XMarkIcon} />
          </Button>

          <Box>
            <PrimaryIndustrySearch
              {...rest}
              label="Primary Industry"
              placeholder="Start typing and select *"
              open
              autoFocus
              sx={[
                (theme) => theme.mixins.placeholderColor(formik, fieldName),
                ...castArray(PrimaryIndustrySearchMobileAutoCompleteStyle),
              ]}
              componentsProps={{
                popper: {
                  sx: PrimaryIndustrySearchMobileAutoCompletePopperStyle,
                },
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PrimaryIndustrySearchMobile;
